import { IconLeft } from '@lib/icons'
import { Button } from '@octopus-do/octopus-storybook'
import { openModal } from '@storeOld/reducers/ui'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import api from '@/api'
import { isSubmitHotkey } from '@/utils'

import './LeaveFeedback.css'
import { getClientEnvironment } from './clientEnvironment'

const Container = (props) => (
  <div className="Modal-leave-feedback">
    <div className="Modal-leave-feedback-content">{props.children}</div>
  </div>
)

export const LeaveFeedback = (props) => {
  const dispatch = useDispatch()
  const authenticated = useSelector(({ auth }) => auth.authenticated)

  const [status, setStatus] = useState('idle')
  const [errors, setErrors] = useState({})
  const textareaRef = useRef(null)
  const [textareaRows, setTextareaRows] = useState(4)

  const decideTextareaHeight = () => {
    const textarea = textareaRef.current
    const lines = textarea.value.split('\n').length

    if (lines > 4) {
      setTextareaRows(lines)
    } else {
      setTextareaRows(4)
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    setStatus('loading')

    setErrors({})

    const { elements } = e.target
    const { message } = elements

    if (message.value.length === 0) {
      setStatus('disabled')

      return setErrors({
        message: 'Message is required.',
      })
    }

    const value = props.utm ? `${props.utm}\n${message.value}` : message.value
    props.onSubmit?.(value)

    try {
      if (authenticated) {
        await api.Auth.support({
          message: value,
          source: props.source || 'general',
          environment: getClientEnvironment(),
        })
      }

      setStatus('success')
    } catch (e) {
      if (e.errors && e.errors.recaptcha) {
        setStatus('idle')

        return setErrors({
          message: e.errors.recaptcha,
        })
      }
    }
  }

  const onClickBack = () => {
    if (!props.onBack) {
      return
    }

    dispatch(openModal(props.onBack.to, props.onBack.params || {}))
  }

  const onKeyDown = (e) => {
    if (!isSubmitHotkey(e) || status === 'loading') {
      return
    }

    e.preventDefault()
    e.currentTarget.form?.requestSubmit()
  }

  if (status === 'success') {
    return (
      <Container>
        <div className="Modal-leave-feedback-header">{props.closeButton}</div>

        <div className="Modal-leave-feedback-success">
          <div className="illustration">
            <svg
              width="80"
              height="138"
              viewBox="0 0 80 138"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M73.0061 50.2741H62.8325C62.1827 50.2741 61.5775 49.9436 61.2099 49.3901C60.8438 48.8381 60.7618 48.1347 60.9951 47.5085L65.1394 36.3689L65.1758 36.2721C66.2237 33.2999 66.7523 30.1874 66.7523 27.0107C66.7523 12.1168 55.0322 0 40.6255 0C26.2197 0 14.4977 12.1168 14.4977 27.0107C14.4977 28.8688 14.682 30.7234 15.0428 32.5243C15.2785 33.699 15.6009 34.8848 15.9991 36.047C16.0078 36.0701 16.0146 36.0916 16.0199 36.1142L16.3056 36.8978L16.3385 36.9916L20.1662 47.523C20.3936 48.1482 20.3107 48.8486 19.9412 49.3982C19.5751 49.9452 18.9703 50.2736 18.3229 50.2736H8.2458C4.3871 50.2736 1.25 53.5175 1.25 57.5069C1.25 61.4934 4.3871 64.7388 8.2458 64.7388H18.9475C19.7453 64.7388 20.464 65.2361 20.7675 65.9992C21.0721 66.7613 20.8999 67.6387 20.332 68.2203L12.5031 76.2333C11.1894 77.5926 10.4601 79.4091 10.4601 81.3404C10.4601 83.2727 11.1894 85.0882 12.5109 86.4549C13.8304 87.8212 15.5888 88.5722 17.4563 88.5722C19.3253 88.5722 21.0789 87.8232 22.395 86.4629L30.2768 78.3948C30.6526 78.0087 31.1536 77.8062 31.6608 77.8062C31.9154 77.8062 32.1729 77.8563 32.4188 77.9621C33.151 78.2785 33.6277 79.0185 33.6277 79.8392V90.9834C33.6277 94.9708 36.7673 98.2162 40.6235 98.2162C44.4813 98.2162 47.6208 94.9708 47.6208 90.9834V79.8493C47.6208 79.0275 48.097 78.2865 48.8322 77.9701C49.5645 77.6527 50.4107 77.8262 50.9742 78.4048L58.8143 86.4494C60.1411 87.8212 61.8985 88.5722 63.766 88.5722C65.634 88.5722 67.3915 87.8212 68.7125 86.4549C70.0344 85.0877 70.7613 83.2732 70.7613 81.3404C70.7613 79.4091 70.031 77.5906 68.7076 76.2208L60.9058 68.2173C60.3399 67.6357 60.1702 66.7603 60.4733 65.9982C60.7778 65.2361 61.4945 64.7388 62.2923 64.7388H73.0052C76.8614 64.7388 80 61.4934 80 57.5069C80.0005 53.518 76.8624 50.2741 73.0061 50.2741ZM33.1198 21.3611C29.9226 21.3611 27.3214 24.05 27.3214 27.3556C27.3214 30.6606 29.9226 33.35 33.1198 33.35C36.3166 33.35 38.9178 30.6606 38.9178 27.3556C38.9178 24.05 36.3166 21.3611 33.1198 21.3611ZM53.3944 31.5963C51.1355 33.9327 47.4562 33.9317 45.1964 31.5963C42.9346 29.2569 42.9346 25.4544 45.1964 23.116C47.4553 20.7816 51.1341 20.7806 53.3944 23.1175C54.2881 24.0425 55.7386 24.0425 56.6333 23.1175C57.5271 22.1935 57.5271 20.6949 56.6333 19.7708C52.5869 15.5888 46.0038 15.5853 41.958 19.7698C41.6874 20.0501 41.4376 20.3424 41.2034 20.6457C39.2995 18.199 36.3855 16.6277 33.1203 16.6277C27.3985 16.6277 22.7435 21.4409 22.7435 27.3561C22.7435 33.2714 27.3985 38.0846 33.1203 38.0846C36.3855 38.0846 39.2995 36.5128 41.2034 34.0666C41.4376 34.3699 41.6869 34.6617 41.958 34.9425C43.9807 37.0342 46.6381 38.0801 49.2956 38.0801C51.9531 38.0801 54.6092 37.0342 56.6333 34.9425C57.5271 34.0184 57.5271 32.5198 56.6333 31.5963C55.7386 30.6707 54.2881 30.6707 53.3944 31.5963Z"
                fill="#18C4A9"
              />
              <ellipse
                cx="40"
                cy="134.27"
                rx="40"
                ry="3.72973"
                fill="#E8EFF7"
              />
              <rect
                x="3.75"
                y="93.2432"
                width="2.5"
                height="14.9189"
                rx="1"
                fill="#E8EFF7"
              />
              <rect
                x="28.75"
                y="105.676"
                width="2.5"
                height="14.9189"
                rx="1"
                fill="#E8EFF7"
              />
              <rect
                x="51.25"
                y="105.676"
                width="2.5"
                height="14.9189"
                rx="1"
                fill="#E8EFF7"
              />
              <rect
                x="75"
                y="93.2432"
                width="2.5"
                height="14.9189"
                rx="1"
                fill="#E8EFF7"
              />
            </svg>
          </div>

          <div className="title">Thank you!</div>

          <div className="description">
            <p>
              {props.successText || (
                <>
                  Your feedback was successfully sent. <br /> We read every
                  message.
                </>
              )}
            </p>
          </div>

          <Button label="Ok" onClick={props.closeModal} />
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <div className="Modal-leave-feedback-header">
        <div className="Modal-leave-feedback-heading">
          {props.returnAction && (
            <button type="button" onClick={props.returnAction}>
              <svg
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.41406 11.6094C8.36719 11.6562 8.29688 11.6797 8.22656 11.6797C8.13281 11.6797 8.0625 11.6562 8.01562 11.6094L3.11719 6.6875C3.04688 6.64062 3.02344 6.59375 3.02344 6.5C3.02344 6.42969 3.04688 6.35938 3.11719 6.3125L8.01562 1.39062C8.0625 1.34375 8.13281 1.32031 8.22656 1.32031C8.29688 1.32031 8.36719 1.34375 8.41406 1.39062L8.88281 1.85938C8.92969 1.90625 8.97656 1.97656 8.97656 2.04688C8.97656 2.14062 8.92969 2.21094 8.88281 2.25781L4.64062 6.5L8.88281 10.7422C8.92969 10.8125 8.97656 10.8828 8.97656 10.9531C8.97656 11.0469 8.92969 11.0938 8.88281 11.1406L8.41406 11.6094Z"
                  fill="#768BA7"
                />
              </svg>
            </button>
          )}
          {props.title || 'Leave feedback'}
        </div>

        {props.onBack && (
          <div className="Modal-leave-feedback-back" onClick={onClickBack}>
            <IconLeft size={16} />
            <div>Back</div>
          </div>
        )}

        {props.closeButton}
      </div>

      <div className="Modal-leave-feedback-description">
        <p>
          {props.text ||
            'Your thoughts, suggestions, requests, just anything you would like to share with us.'}
        </p>
      </div>

      <form className="Modal-leave-feedback-form" onSubmit={onSubmit}>
        <div>
          <textarea
            placeholder="Write a message..."
            name="message"
            ref={textareaRef}
            rows={textareaRows}
            onKeyDown={onKeyDown}
            onChange={() => {
              setStatus('idle')
              decideTextareaHeight()
              setErrors({})
            }}
          />

          {errors.message && <small className="error">{errors.message}</small>}
        </div>

        <Button
          type="submit"
          label="Send"
          loading={status === 'loading'}
          disabled={status === 'disabled'}
        />
      </form>
    </Container>
  )
}
